import React, { useCallback, useState } from "react";
import { useAppSelector, useAppDispatch } from "./hooks/reduxHooks";
import { useFeatureFlag } from "./hooks/useFeatureFlag";
import { useStreamingMessage } from "./hooks/useStreamingMessage";
import { selectResultsForTurn, selectResearchDocsForTurn } from "./ResultsSlice_Store";
import { openDealDetail, fetchDealDetail } from "./DealsTableSlice_Store";
import { openResearchDoc, fetchResearchDocDetail } from "./ResearchDocsSlice_Store";
import { openDocumentPreview } from "./DocumentPreviewSlice_Store";
import { setLayoutMode } from "./LayoutSlice_Store";
import { canPreviewDocument } from "./services/documentPreviewService";
import InlineDealsTable from "./InlineDealsTable_Component";
import InlineResearchDocsTable from "./InlineResearchDocsTable_Component";
import NonPreviewableDocumentModal from "./components/NonPreviewableDocumentModal";
import type { Deal, ResearchDoc } from "./types/redux";
import { decideTabVisibility, type ResultsTab } from "./utils/inlineResultsTabs";

interface Props {
  turnId: string | undefined;
}

export default function InlineResultsTabs({ turnId }: Props): JSX.Element | null {
  const dispatch = useAppDispatch();
  const flagOn = useFeatureFlag("inlineDeals");
  const turnResults = useAppSelector((state) => selectResultsForTurn(state, turnId));
  const researchDocs: ResearchDoc[] = useAppSelector((state) =>
    selectResearchDocsForTurn(state, turnId)
  );
  const streamingData = useStreamingMessage(turnId || "");

  const deals: Deal[] = turnResults?.deals ?? [];
  const documents = turnResults?.documents ?? [];

  const visibility = decideTabVisibility(deals.length, researchDocs.length);
  const [activeTab, setActiveTab] = useState<ResultsTab | null>(visibility.defaultTab);
  const [nonPreviewableDoc, setNonPreviewableDoc] = useState<ResearchDoc | null>(null);

  const handleDealClick = useCallback((deal: Deal) => {
    const dealId = deal.dealId || deal.id;
    if (!dealId) return;
    dispatch(openDealDetail(dealId));
    dispatch(fetchDealDetail(dealId));
    dispatch(setLayoutMode("workspace"));
  }, [dispatch]);

  const handleDocClick = useCallback((doc: ResearchDoc) => {
    if (!doc.docId) return;
    // Track selection in ResearchDocsSlice. Only fire the detail-fetch thunk
    // when a dedicated research-docs base URL is actually configured — the
    // endpoint is still stubbed (Q4), so an unconditional dispatch in
    // unconfigured environments just populates detailError with noise.
    dispatch(openResearchDoc(doc.docId));
    if (import.meta.env.VITE_RESEARCH_DOCS_API_BASE_URL) {
      dispatch(fetchResearchDocDetail(doc.docId));
    }

    // Non-previewable types (e.g. .xlsx without SharePoint IDs) fall through
    // to the explanatory modal — same UX as fact-badge clicks.
    if (!canPreviewDocument(doc as unknown as Parameters<typeof canPreviewDocument>[0])) {
      setNonPreviewableDoc(doc);
      return;
    }

    // Mirror DealDocsPanel + InlineFactBadge: normalize docId onto the
    // payload and switch to workspace so the RHS pane is visible. Preserve
    // originalFilename intact — the preview pane reads it for file-type
    // detection. The tab label prefers researchTitle (see DealsLayout).
    dispatch(openDocumentPreview({ ...doc, docId: doc.docId }));
    dispatch(setLayoutMode("workspace"));
  }, [dispatch]);

  const isStreaming = !!streamingData.displayedText && !(streamingData.isComplete && !streamingData.isAnimating);

  if (!flagOn || isStreaming) return null;
  if (!visibility.defaultTab) return null;

  const current = activeTab ?? visibility.defaultTab;

  const dealsTable = (
    <InlineDealsTable
      deals={deals}
      documents={documents}
      onDealClick={handleDealClick}
    />
  );

  const researchTable = (
    <InlineResearchDocsTable
      researchDocs={researchDocs}
      onDocClick={handleDocClick}
    />
  );

  const nonPreviewableModal = (
    <NonPreviewableDocumentModal
      isOpen={!!nonPreviewableDoc}
      onClose={() => setNonPreviewableDoc(null)}
      documentName={
        nonPreviewableDoc?.researchTitle ||
        nonPreviewableDoc?.originalFilename ||
        nonPreviewableDoc?.documentName ||
        "Unknown document"
      }
      filePath={nonPreviewableDoc?.filePath ?? undefined}
      sharepointSiteId={nonPreviewableDoc?.sharepointSiteId ?? undefined}
      sharepointDriveId={nonPreviewableDoc?.sharepointDriveId ?? undefined}
      sharepointItemId={nonPreviewableDoc?.sharepointItemId ?? undefined}
    />
  );

  if (!visibility.showTabChrome) {
    return (
      <>
        {visibility.showDeals ? dealsTable : researchTable}
        {nonPreviewableModal}
      </>
    );
  }

  return (
    <div>
      <div
        role="tablist"
        aria-label="Results"
        className="flex items-center gap-1 mt-3 border-b border-cvc-tableBorder"
      >
        <TabButton
          label={`Deals (${deals.length})`}
          active={current === "deals"}
          onClick={() => setActiveTab("deals")}
        />
        <TabButton
          label={`Research (${researchDocs.length})`}
          active={current === "research"}
          onClick={() => setActiveTab("research")}
        />
      </div>
      {current === "deals" ? dealsTable : researchTable}
      {nonPreviewableModal}
    </div>
  );
}

interface TabButtonProps {
  label: string;
  active: boolean;
  onClick: () => void;
}

function TabButton({ label, active, onClick }: TabButtonProps): JSX.Element {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={`px-3 py-1.5 text-sm font-cvcBody transition-colors border-b-2 -mb-px ${
        active
          ? "border-cvc-blue text-cvc-blue"
          : "border-transparent text-cvc-headerText hover:text-cvc-blue"
      }`}
    >
      {label}
    </button>
  );
}
